import { crmEntityType } from "../../modules/overviewAnalysis/entities.mjs";
import type { EntityAnalysisRequest } from "./overviewBankAnalysis";

export interface BankBreakdownRow { label: string; count: number; unknown?: boolean }
export interface BankDeclineReason extends BankBreakdownRow { key: string }
export interface BankAnalysisDetailsData {
	period: { from: string; to: string };
	currency: string;
	total: number;
	reasons: BankDeclineReason[];
	countries: BankBreakdownRow[];
	banks: BankBreakdownRow[];
	emails: BankBreakdownRow[];
	cards: BankBreakdownRow[];
	errorCount: number;
}

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const count = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const normalized = (label: string) => label.trim().replace(/\s+/g, " ").toLowerCase();

// Translate known CRM messages only. Unknown reasons remain visible verbatim.
const reason = (label: string): { key: string; label: string } => {
	const key = normalized(label);
	const names: Record<string, [string, string]> = {
		"google pay token is required": ["google_pay_token", "Отсутствует или некорректен токен Google Pay"],
		"google pay token format not valid: undefined": ["google_pay_token", "Отсутствует или некорректен токен Google Pay"],
		"wrong params": ["wrong_params", "Некорректные параметры запроса"],
		"card bin error: wrong pan": ["wrong_pan", "Ошибка BIN: некорректный номер карты"],
		"no active channel for given terminal": ["no_channel", "Нет активного канала для терминала"],
		"decline, suspected fraud": ["fraud", "Подозрение на мошенничество"],
		"decline, not sufficient funds / over credit limit": ["funds", "Недостаточно средств или превышен кредитный лимит"],
		"decline, refer to card issuer's special conditions": ["conditions", "Ограничения банка по карте"],
		"decline, security violation": ["security", "Отказ по требованиям безопасности"],
		"blocked by cardholder/contact cardholder": ["cardholder", "Блокировка держателем карты — требуется связаться с ним"],
		"status: 0, message: not authenticated, detail: 08: no card record": ["no_card", "Карта не найдена при аутентификации"],
		"payment in progress": ["pending", "Платёж ещё обрабатывается"],
		"decline, invalid card number": ["invalid_card", "Некорректный номер карты"],
		"decline, expired card": ["expired_card", "Срок действия карты истёк"],
	};
	if (Object.hasOwn(names, key)) return { key: names[key][0], label: names[key][1] };
	if (key === "3ds authentication failed, transstatus: n" || (key.startsWith("status: 0, message: n-status/challenge authentication via acs:") && key.endsWith("detail: 01: card authentication failed"))) {
		return { key: "3ds", label: "Не пройдена аутентификация 3-D Secure" };
	}
	return { key: `raw:${key}`, label: label.trim() };
};

function rows(value: unknown, total: number, unknownLabel?: string): BankBreakdownRow[] {
	if (value === undefined) return [];
	if (!Array.isArray(value)) throw new Error("Invalid breakdown");
	let sum = 0;
	return value.map(row => {
		if (!record(row) || typeof row.label !== "string" || !count(row.count)) throw new Error("Invalid row");
		const unknown = !row.label.trim() || row.label.trim() === "—";
		if (unknown && !unknownLabel) throw new Error("Invalid label");
		sum += row.count;
		if (!Number.isSafeInteger(sum) || sum > total) throw new Error("Inconsistent breakdown");
		return unknown ? { label: unknownLabel!, count: row.count, unknown: true } : { label: row.label, count: row.count };
	}).filter(row => row.count > 0);
}

function merge<T extends BankBreakdownRow>(values: T[], key: (row: T) => string): T[] {
	const result = new Map<string, T>();
	for (const row of values) {
		const id = key(row), existing = result.get(id);
		if (existing) existing.count += row.count;
		else result.set(id, { ...row });
	}
	return [...result.values()].sort((a, b) => b.count - a.count);
}

export function parseBankAnalysis(raw: string, expected: EntityAnalysisRequest): BankAnalysisDetailsData | null {
	try {
		const response: unknown = JSON.parse(raw);
		if (!record(response) || response.error || !record(response.data)) return null;
		const data = response.data;
		// Without a currency filter, CRM may return its resolved/default currency.
		// Explicit currency requests must still match exactly.
		const currencyMatches = expected.currency ? data.currency === expected.currency
			: typeof data.currency === "string" && (data.currency === "" || /^[A-Z]{3}$/.test(data.currency));
		if (!record(data.entity) || data.entity.type !== (expected.bank.type === "card" ? "card" : crmEntityType(expected.bank)) || data.entity.value !== expected.bank.value ||
			!record(data.period) || data.period.from !== expected.from || data.period.to !== expected.to ||
			!currencyMatches || !count(data.total_declines) || !record(data.breakdowns)) return null;
		const total = data.total_declines, breakdowns = data.breakdowns;
		const errors = rows(breakdowns.errors, total);
		return {
			period: { from: expected.from, to: expected.to },
			currency: data.currency as string,
			total,
			reasons: merge(errors.map(row => ({ ...reason(row.label), count: row.count })), row => row.key),
			errorCount: errors.reduce((sum, row) => sum + row.count, 0),
			countries: merge(rows(breakdowns.countries, total, "Страна не указана").map(row => ({ ...row, label: row.unknown ? row.label : row.label.trim().toUpperCase() })), row => row.label),
			banks: merge(rows(breakdowns.banks, total, "Банк не указан"), row => row.label),
			emails: merge(rows(breakdowns.emails, total, "Email не указан"), row => row.label),
			cards: merge(rows(breakdowns.cards, total, "Карта не указана"), row => row.label),
		};
	} catch { return null; }
}

export const bankCount = (value: number, language: "en" | "ru" = "ru") => value.toLocaleString(language === "ru" ? "ru-RU" : "en-US");
export const declineShare = (value: number, total: number, language: "en" | "ru" = "ru") => total > 0 ? `${(value / total * 100).toLocaleString(language === "ru" ? "ru-RU" : "en-US", { maximumFractionDigits: 1 })}%` : "—";
export const bankCountryName = (code: string, language: "en" | "ru" = "ru") => {
	try { return new Intl.DisplayNames([language], { type: "region" }).of(code) || code; } catch { return code; }
};

export function bankDeclineSummary(data: BankAnalysisDetailsData): { text: string; action?: string } {
	if (data.total === 0) return { text: "За выбранный период отказов не найдено." };
	const top = data.reasons[0];
	if (!top) return { text: `CRM вернула отказов: ${bankCount(data.total)}. Разбивка по причинам отсутствует.` };
	const actions: Record<string, string> = {
		google_pay_token: "Проверьте получение токена Google Pay и его передачу в платёжный запрос: CRM сообщает об отсутствующем или некорректном токене.",
		wrong_params: "Проверьте обязательные параметры платёжного запроса и их формат.",
		wrong_pan: "Проверьте номер карты и параметры, переданные для определения BIN.",
		no_channel: "Проверьте активные каналы и настройки маршрутизации терминала.",
		fraud: "Проверьте причины срабатывания антифрода у провайдера и банка. Эта причина отказа сама по себе не подтверждает мошенничество.",
		funds: "Проверьте повторные попытки оплаты: клиенту может потребоваться пополнить счёт или использовать другую карту.",
		conditions: "Уточните у провайдера или банка, какие ограничения по карте вызвали отказы.",
		security: "Уточните у провайдера или банка, какие проверки безопасности отклоняют платежи.",
		"3ds": "Проверьте прохождение 3-D Secure и ответы сервиса аутентификации банка.",
		pending: "Проверьте финальные статусы: CRM включила в отказы платежи, которые ещё обрабатываются.",
	};
	return {
		text: `Самая частая причина в ответе — «${top.label}»: ${bankCount(top.count)} из ${bankCount(data.total)} отказов (${declineShare(top.count, data.total)}).`,
		action: actions[top.key] || "Проверьте сообщения об отказах и повторные попытки по картам в деталях ниже.",
	};
}
